/**
 * A FICHA DO LOTE — o que o gestor quer ver de um ciclo ativo, num lugar só.
 *
 * Pedido em 22/09/2026, e ele escreveu praticamente a especificação:
 *
 *   «A1 10600 peixes, hoje estimado em 80 a 120 gramas, consumo nesta fase de
 *    1,2 a 1,6 % PV, previsão de consumo de Y kg de ração, recomendável N
 *    tratos por dia para esta fase. Biomassa Estimada 1,1 t hoje, Carga
 *    Represa 2,5 T/ha. Consumo acumulado até o momento 15.125 kg de ração
 *    desde início do ciclo»
 *
 * Repare que ele pede FAIXAS — «80 a 120 gramas», «1,2 a 1,6 % PV». Não é
 * detalhe de redação: é a regra 8 pedida por quem vai ler, e por isso este
 * módulo devolve faixas e não números únicos onde a coisa é estimada.
 *
 * ── O QUE É MEDIDO E O QUE É ESTIMADO, SEPARADO NA ORIGEM ────────────────
 *
 * Só DOIS números aqui são medidos: os peixes que entraram e a ração
 * acumulada. Todo o resto pende da estimativa de peso, que pende da curva.
 * Por isso o tipo separa os dois — a tela não tem como misturá-los sem querer,
 * e quem ler o código vê a fronteira.
 *
 * ── E A PREVISÃO DE CONSUMO RESPEITA O TETO DA ÁGUA ──────────────────────
 *
 * Da metade do ciclo em diante, quem decide quanto o peixe come nesta fazenda
 * não é o apetite dele — é o teto da água. Uma previsão que ignorasse isso
 * mandaria comprar ração para um consumo que a represa não aceita.
 *
 * Quando o teto morde, a faixa encolhe por cima: «13 a 15 kg» em vez de «13 a
 * 18», e a ficha diz que quem mandou foi a água. É a mesma conta do Trato
 * (`metaDoDia`), e é de propósito: duas contas do mesmo número divergiriam.
 */

import type { FaixaDeArracoamento } from '../acesso/api'
import { metaDoDia } from './calculos'
import { fracao, kg, kgHaDia, m2, m2ParaHa } from './unidades'

/** O que veio do banco, antes de qualquer conta. */
export interface DadosDoLote {
  /** Peixes vivos hoje: entraram menos o que saiu. Medido. */
  peixes: number | null
  /** Peso médio estimado hoje, pela curva, ancorado na última pesagem. */
  peso_hoje_g: number | null
  /** A faixa do peso estimado — o erro da curva aplicado ao GANHO. */
  peso_min_g: number | null
  peso_max_g: number | null
  area_m2: number
  tanque_id: string
  /** Quilos de ração que este ciclo já recebeu. Medido, soma de lançamentos. */
  racao_acumulada_kg: number
}

export interface ResumoDoLote {
  biomassa_kg: number
  carga_t_ha: number
  /** A faixa da tabela de arraçoamento que cobre o peso de hoje. */
  faixa: FaixaDeArracoamento
  /** O que o peixe pediria, em quilos por dia. */
  demanda_kg: { min: number; max: number }
  /** O que a água aceita por dia, em quilos. Nulo sem teto cadastrado. */
  teto_kg: number | null
  /** O menor dos dois, que é o que se deve oferecer. */
  previsto_kg: { min: number; max: number }
  /** Verdadeiro quando o teto da água encolheu a previsão. */
  aguaManda: boolean
}

/**
 * Por que a ficha não fecha, quando não fecha.
 *
 * Mesmo padrão de `porQueNaoApura` na pesagem e de `porQueNaoDaParaEstimar` no
 * estoque: quem devolve nulo diz o motivo, e a tela não tem como escolher a
 * frase errada. Foi assim que o galpão passou a culpar o trato pelo que era
 * falta de compra.
 */
export type SemResumo =
  | 'sem_peixe'
  | 'sem_peso'
  | 'fora_da_tabela'

export function porQueNaoResume(
  lote: DadosDoLote,
  arracoamento: readonly FaixaDeArracoamento[],
): SemResumo | null {
  if (lote.peixes === null || !Number.isFinite(lote.peixes) || lote.peixes <= 0) {
    return 'sem_peixe'
  }
  if (lote.peso_hoje_g === null || !Number.isFinite(lote.peso_hoje_g) || lote.peso_hoje_g <= 0) {
    return 'sem_peso'
  }
  if (faixaDoPeso(lote.peso_hoje_g, arracoamento) === null) return 'fora_da_tabela'
  return null
}

/** A faixa da tabela que cobre este peso. Intervalo [de, ate). */
function faixaDoPeso(
  pesoG: number,
  arracoamento: readonly FaixaDeArracoamento[],
): FaixaDeArracoamento | null {
  return arracoamento.find((f) => pesoG >= f.peso_de_g && pesoG < f.peso_ate_g) ?? null
}

/**
 * Monta a ficha. Nulo quando falta matéria-prima — `porQueNaoResume` diz o quê.
 *
 * `teto` vem de `tetoDoTanque`, que já escolhe a linha certa pela estação. Nulo
 * é resposta legítima: tanque sem teto cadastrado ainda tem previsão, só não
 * tem limite de água — e a ficha diz isso em vez de inventar um teto.
 */
export function resumoDoLote(
  lote: DadosDoLote,
  arracoamento: readonly FaixaDeArracoamento[],
  teto: number | null,
): ResumoDoLote | null {
  if (porQueNaoResume(lote, arracoamento) !== null) return null
  const peixes = lote.peixes as number
  const pesoHoje = lote.peso_hoje_g as number
  const faixa = faixaDoPeso(pesoHoje, arracoamento) as FaixaDeArracoamento

  const biomassa = (peixes * pesoHoje) / 1000
  const area = m2(lote.area_m2)
  const hectares = m2ParaHa(area)

  const comTeto = (pct: number) =>
    teto === null
      ? { meta: kg(biomassa * (pct / 100)), quemManda: 'peixe' as const }
      : metaDoDia(kg(biomassa), fracao(pct / 100), kgHaDia(teto), area)

  const baixo = comTeto(faixa.pct_pv_min)
  const alto = comTeto(faixa.pct_pv_max)

  return {
    biomassa_kg: biomassa,
    carga_t_ha: hectares > 0 ? biomassa / 1000 / hectares : 0,
    faixa,
    demanda_kg: {
      min: biomassa * (faixa.pct_pv_min / 100),
      max: biomassa * (faixa.pct_pv_max / 100),
    },
    teto_kg: teto === null ? null : teto * hectares,
    previsto_kg: { min: baixo.meta, max: alto.meta },
    // A ÁGUA MANDOU se ela encolheu o TOPO da faixa. Pelo piso seria tarde
    // demais: quando até o mínimo já bate no teto, o tanque passou do ponto
    // há semanas, e a ficha precisa avisar antes disso.
    aguaManda: alto.quemManda === 'agua',
  }
}

/**
 * QUANTOS TRATOS POR DIA, para aquela fase.
 *
 * «3 a 4 na alevinagem, 2 nas demais fases e às vezes 1 na reta final» —
 * está no CLAUDE.md desde o começo, e nunca tinha saído de lá para a tela.
 *
 * Vem de `parametros`, e não daqui: é regra da fazenda, e outra piscicultura
 * trata em outro ritmo (regra 10). O formato é o mesmo das outras listas do
 * projeto — `alevinagem:3-4;juvenil:2;engorda:2;misto:2` —, separado por ponto
 * e vírgula porque a vírgula é o decimal.
 *
 * Devolve o RÓTULO e não um número: «3 a 4» é a resposta certa para a
 * alevinagem, e arredondar para 3 ou para 4 seria escolher por quem trata.
 */
export function tratosPorDia(valor: string | null, fase: string): string | null {
  if (valor === null) return null
  for (const parte of valor.split(';')) {
    const [nome, quantos] = parte.split(':').map((x) => x.trim())
    if (nome === undefined || quantos === undefined || quantos === '') continue
    if (nome.toLowerCase() !== fase.toLowerCase()) continue
    return quantos.includes('-') ? quantos.split('-').join(' a ') : quantos
  }
  return null
}
